import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead, FormControlLabel,
    TableRow,
    Paper,
    Stack,
    Grid,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import axios from "axios";

// Styled Paper
const StyledPaper = styled(Paper)(({ theme, mode, customColors }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    backgroundColor:
        mode === "dark"
            ? "#2A2A3C"
            : mode === "custom"
                ? customColors?.background || "#f5f5f5"
                : "#f0f7ff",
    borderRadius: "8px",
    boxShadow:
        mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.6)"
            : "0 2px 8px rgba(0,0,0,0.15)",
}));

// Palette setup
const palette = {
    light: {
        headerBg: "#e3f2fd",
        headerText: "#37474f",
        rowHover: "#f5f5f5",
        text: "#333",
    },
    dark: {
        headerBg: "#2A2A3C",
        headerText: "#EDEDED",
        rowHover: "#383C54",
        text: "#EDEDED",
    },
    custom: (customColors) => ({
        headerBg: customColors?.cardBg || "#f5f5f5",
        headerText: customColors?.text || "#111111",
        rowHover: customColors?.primary || customColors?.primary || "#e8f4ff",
        text: customColors?.text || "#111111",
    }),
};

export default function MyPermissionsPanel({
    mode,
    customColors,
    setSnackbar,
    isLoading,
    setIsLoading,
    currentUserRole,
}) {
    // ========== STATE ==========
    const [myPermissions, setMyPermissions] = useState({});
    const apiUrl = process.env.REACT_APP_BASE_URL;

    // ========== EFFECTS ==========
    // Fetch current user's permissions
    useEffect(() => {
        const fetchMyPermissions = async () => {
            setIsLoading(true);
            try {
                const token = sessionStorage.getItem("authToken");
                const response = await axios.get(`${apiUrl}/user/permissions/`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setMyPermissions(response.data.permissions || {});
            } catch (error) {
                console.error("Failed to fetch permissions:", error);
                setSnackbar({
                    open: true,
                    message: error.response?.data?.error || error.response?.data?.message || "Failed to fetch permissions. Please try again.",
                    severity: "error",
                });
                setMyPermissions({});
            } finally {
                setIsLoading(false);
            }
        };
        fetchMyPermissions();
    }, [setIsLoading, setSnackbar]);

    // ========== HELPERS ==========
    // Helper function to group permissions by resource with CRUD separation
    const groupPermissionsByResource = (permissions) => {
        const crudGroups = {};
        const otherGroups = {};
        const crudActions = ['create', 'read', 'update', 'delete'];

        Object.keys(permissions).forEach(key => {
            if (key === 'id' || key === 'role') return;

            // Extract resource and action
            const match = key.match(/can_(create|read|update|delete|view|start|submit|register|change|set|import|export|reset|bulk|enable)_(.+)/);
            if (match) {
                const [, action, resource] = match;
                const resourceKey = resource.replace(/_/g, ' ');

                if (crudActions.includes(action)) {
                    // CRUD operations
                    if (!crudGroups[resourceKey]) {
                        crudGroups[resourceKey] = {};
                    }
                    crudGroups[resourceKey][action] = { key, value: permissions[key] };
                } else {
                    // Other operations
                    if (!otherGroups[resourceKey]) {
                        otherGroups[resourceKey] = {};
                    }
                    otherGroups[resourceKey][action] = { key, value: permissions[key] };
                }
            }
        });

        return { crudGroups, otherGroups };
    };

    // ========== RENDER ==========
    return (
        <>
            <StyledPaper
                sx={{
                    backgroundColor: mode === "dark"
                        ? palette.dark.headerBg
                        : mode === "custom"
                            ? customColors?.background || "#f5f5f5"
                            : palette.light.headerBg,
                    borderRadius: "16px",
                    boxShadow: mode === "dark"
                        ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                        : "0 8px 24px rgba(30, 139, 175, 0.12)",
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        mb: 3,
                        fontWeight: 700,
                        letterSpacing: "-0.5px",
                    }}
                >
                    My Permissions
                </Typography>

                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress sx={{ color: "#1e8bafff" }} size={48} />
                    </Box>
                ) : Object.keys(myPermissions).length > 0 ? (
                    <Box>
                        {(() => {
                            const { crudGroups, otherGroups } = groupPermissionsByResource(myPermissions);
                            const hasCrud = Object.keys(crudGroups).length > 0;
                            const hasOther = Object.keys(otherGroups).length > 0;

                            return (
                                <>
                                    {/* CRUD Operations Table */}
                                    {hasCrud && (
                                        <Box sx={{ mb: 4 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 2,
                                                    color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                                }}
                                            >
                                                CRUD Permissions
                                            </Typography>
                                            <TableContainer
                                                sx={{
                                                    border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(30, 139, 175, 0.15)",
                                                    borderRadius: "12px",
                                                    overflow: "hidden",
                                                    boxShadow: mode === "dark" ? "0 4px 12px rgba(0, 0, 0, 0.2)" : "0 4px 12px rgba(30, 139, 175, 0.08)",
                                                }}
                                            >
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow sx={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)" }}>
                                                            <TableCell sx={{ color: "#ffffff", fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.5px", py: 2, minWidth: 180 }}>
                                                                RESOURCE
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: "#ffffff", fontWeight: 700, fontSize: "0.9rem", py: 2, width: 100 }}>
                                                                CREATE
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: "#ffffff", fontWeight: 700, fontSize: "0.9rem", py: 2, width: 100 }}>
                                                                READ
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: "#ffffff", fontWeight: 700, fontSize: "0.9rem", py: 2, width: 100 }}>
                                                                UPDATE
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: "#ffffff", fontWeight: 700, fontSize: "0.9rem", py: 2, width: 100 }}>
                                                                DELETE
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {Object.entries(crudGroups).map(([resource, actions], index) => (
                                                            <TableRow
                                                                key={resource}
                                                                sx={{
                                                                    backgroundColor: mode === "dark"
                                                                        ? index % 2 === 0 ? "rgba(255, 255, 255, 0.03)" : "transparent"
                                                                        : index % 2 === 0 ? "rgba(30, 139, 175, 0.03)" : "transparent",
                                                                    transition: "all 0.2s ease",
                                                                    "&:hover": {
                                                                        backgroundColor: mode === "dark" ? "rgba(30, 139, 175, 0.1)" : "rgba(30, 139, 175, 0.08)",
                                                                    },
                                                                }}
                                                            >
                                                                <TableCell sx={{ color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.text || "#111111" : palette.light.text, textTransform: 'capitalize', fontWeight: 600, fontSize: "0.9rem" }}>
                                                                    {resource}
                                                                </TableCell>
                                                                {['create', 'read', 'update', 'delete'].map(action => (
                                                                    <TableCell key={action} align="center">
                                                                        {actions[action] ? (
                                                                            <Tooltip
                                                                                title={action.charAt(0).toUpperCase() + action.slice(1) + " " + resource}
                                                                                arrow
                                                                            >
                                                                                <CheckCircleOutlineIcon
                                                                                    sx={{
                                                                                        color: "#4caf50",
                                                                                        fontSize: 20,
                                                                                    }}
                                                                                />
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Tooltip
                                                                                title={action.charAt(0).toUpperCase() + action.slice(1) + " " + resource}
                                                                                arrow
                                                                            >
                                                                                <CancelOutlinedIcon
                                                                                    sx={{
                                                                                        color: "#d32f2f",
                                                                                        fontSize: 20,
                                                                                    }}
                                                                                />
                                                                            </Tooltip>
                                                                        )}
                                                                    </TableCell>
                                                                ))}
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}

                                    {/* Other Permissions - Grouped */}
                                    {hasOther && (
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 2,
                                                    color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                                }}
                                            >
                                                Other Permissions
                                            </Typography>
                                            {Object.entries(otherGroups).map(([resource, actions]) => (
                                                <Box
                                                    key={resource}
                                                    sx={{
                                                        mb: 3,
                                                        p: 2,
                                                        border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(30, 139, 175, 0.15)",
                                                        borderRadius: "12px",
                                                        backgroundColor: mode === "dark" ? "rgba(30, 139, 175, 0.1)" : "rgba(30, 139, 175, 0.08)",
                                                        boxShadow: mode === "dark" ? "0 4px 12px rgba(0, 0, 0, 0.2)" : "0 4px 12px rgba(30, 139, 175, 0.08)",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            mb: 2,
                                                            color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                                            textTransform: 'capitalize'
                                                        }}
                                                    >
                                                        {resource}
                                                    </Typography>
                                                    <Grid container spacing={1}>
                                                        {Object.entries(actions).map(([action, { key, value }]) => (
                                                            <Grid item xs={6} sm={4} md={3} key={key}>
                                                                <FormControlLabel
                                                                    control={
                                                                        value ? (
                                                                            <Tooltip
                                                                                title={action.charAt(0).toUpperCase() + action.slice(1) + " " + resource}
                                                                                arrow
                                                                            >
                                                                                <CheckCircleOutlineIcon
                                                                                    sx={{
                                                                                        color: "#4caf50",
                                                                                        fontSize: 20,
                                                                                    }}
                                                                                />
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <Tooltip
                                                                                title={action.charAt(0).toUpperCase() + action.slice(1) + " " + resource}
                                                                                arrow
                                                                            >
                                                                                <CancelOutlinedIcon
                                                                                    sx={{
                                                                                        color: "#d32f2f",
                                                                                        fontSize: 20,
                                                                                    }}
                                                                                />
                                                                            </Tooltip>
                                                                        )
                                                                    }
                                                                    label={
                                                                        <Typography
                                                                            sx={{
                                                                                color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.text || "#111111" : palette.light.text,
                                                                                fontSize: "0.9rem",
                                                                                fontWeight: "bold",
                                                                            }}
                                                                        >
                                                                            {action.charAt(0).toUpperCase() + action.slice(1)}
                                                                        </Typography>
                                                                    }
                                                                    sx={{
                                                                        m: 0,
                                                                        width: "100%",
                                                                        display: "flex",
                                                                        justifyContent: "flex-start",
                                                                        alignItems: "center",
                                                                    }}
                                                                />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </>
                            );
                        })()}
                    </Box>
                ) : (
                    <Box sx={{ textAlign: "center", py: 8, opacity: 0.7 }}>
                        <LockIcon sx={{ fontSize: 64, color: "#1e8bafff", mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : "#37474f", fontWeight: 500 }}>
                            No permissions available
                        </Typography>
                    </Box>
                )}
            </StyledPaper>
        </>
    );
}
